import { z } from "zod";
import { countries } from "countries-list";

/**
 * Country-specific phone number lengths
 * Dynamically built from countries-list library
 * Common phone length patterns by country code (in digits)
 * Reference: https://en.wikipedia.org/wiki/List_of_country_calling_codes
 */
const COUNTRY_PHONE_PATTERNS: { [key: number]: { min: number; max: number; description?: string } } = {
  // Asia
  91: { min: 10, max: 10, description: "India" },
  86: { min: 11, max: 11, description: "China" },
  81: { min: 10, max: 11, description: "Japan" },
  82: { min: 10, max: 10, description: "South Korea" },
  60: { min: 10, max: 10, description: "Malaysia" },
  65: { min: 8, max: 8, description: "Singapore" },
  66: { min: 9, max: 9, description: "Thailand" },
  84: { min: 10, max: 10, description: "Vietnam" },
  92: { min: 10, max: 10, description: "Pakistan" },
  88: { min: 10, max: 10, description: "Bangladesh" },

  // Europe
  44: { min: 10, max: 11, description: "United Kingdom" },
  33: { min: 9, max: 9, description: "France" },
  49: { min: 10, max: 11, description: "Germany" },
  39: { min: 10, max: 10, description: "Italy" },
  34: { min: 9, max: 9, description: "Spain" },
  31: { min: 9, max: 9, description: "Netherlands" },
  46: { min: 9, max: 9, description: "Sweden" },
  47: { min: 8, max: 8, description: "Norway" },
  41: { min: 9, max: 9, description: "Switzerland" },
  43: { min: 10, max: 10, description: "Austria" },
  32: { min: 9, max: 9, description: "Belgium" },
  45: { min: 8, max: 8, description: "Denmark" },
  358: { min: 9, max: 9, description: "Finland" },
  353: { min: 9, max: 9, description: "Ireland" },
  48: { min: 9, max: 9, description: "Poland" },
  40: { min: 9, max: 9, description: "Romania" },
  36: { min: 9, max: 9, description: "Hungary" },
  420: { min: 9, max: 9, description: "Czech Republic" },
  421: { min: 9, max: 9, description: "Slovakia" },

  // Americas
  1: { min: 10, max: 10, description: "USA/Canada/Caribbean" },
  52: { min: 10, max: 10, description: "Mexico" },
  55: { min: 10, max: 11, description: "Brazil" },
  56: { min: 9, max: 9, description: "Chile" },
  54: { min: 10, max: 10, description: "Argentina" },
  57: { min: 10, max: 10, description: "Colombia" },
  51: { min: 9, max: 9, description: "Peru" },
  58: { min: 10, max: 10, description: "Venezuela" },

  // Africa
  27: { min: 9, max: 9, description: "South Africa" },
  234: { min: 10, max: 10, description: "Nigeria" },
  20: { min: 10, max: 10, description: "Egypt" },
  212: { min: 9, max: 9, description: "Morocco" },
  254: { min: 9, max: 9, description: "Kenya" },
  256: { min: 9, max: 9, description: "Uganda" },

  // Oceania
  61: { min: 9, max: 9, description: "Australia" },
  64: { min: 9, max: 9, description: "New Zealand" },
  63: { min: 10, max: 10, description: "Philippines" },
  62: { min: 10, max: 12, description: "Indonesia" },
  98: { min: 10, max: 10, description: "Iran" },
  966: { min: 9, max: 9, description: "Saudi Arabia" },
  971: { min: 9, max: 9, description: "United Arab Emirates" },
};

/**
 * Build dynamic phone validation map from countries-list
 * This ensures we support all countries from the library
 */
export const buildPhoneLengthMapFromCountries = (): { [key: number]: { min: number; max: number } } => {
  const phoneMap: { [key: number]: { min: number; max: number } } = { ...COUNTRY_PHONE_PATTERNS };

  // Get all unique phone codes from countries-list
  const countryCodes = new Set<number>();
  Object.entries(countries).forEach(([_, data]) => {
    const phones = Array.isArray(data.phone) ? data.phone : [data.phone];
    phones.forEach((phone) => {
      const phoneCode = typeof phone === "string" ? parseInt(phone, 10) : phone;
      if (phoneCode && !phoneMap[phoneCode]) {
        countryCodes.add(phoneCode);
      }
    });
  });

  // For countries not in COUNTRY_PHONE_PATTERNS, use sensible defaults
  // Most countries have phone numbers between 8-15 digits (excluding country code)
  countryCodes.forEach((phoneCode) => {
    if (!phoneMap[phoneCode]) {
      // Default: 8-15 digits for unknown countries
      // Can be refined based on specific country patterns
      phoneMap[phoneCode] = { min: 8, max: 15 };
    }
  });

  return phoneMap;
};

// Generate the phone length map once at module load
const COUNTRY_PHONE_LENGTHS = buildPhoneLengthMapFromCountries();

export const LoginWithPasswordSchema = z.object({
  mobile: z.string().regex(/^\d{5,15}$/, "Invalid mobile number"),
  password: z
    .string()
    .optional()
    .refine((val) => val === undefined || val.length === 0 || val.length >= 6, {
      message: "Password must be at least 6 characters",
    }),
});

// Validation function for country-specific phone numbers
export const validatePhoneForCountry = (phoneNumber: string, countryCode: number): { valid: boolean; message: string } => {
  const lengths = COUNTRY_PHONE_LENGTHS[countryCode];

  if (!phoneNumber) {
    return { valid: false, message: "Phone number is required" };
  }

  if (!lengths) {
    // Default validation for unknown countries: 5-15 digits
    if (!/^\d{5,15}$/.test(phoneNumber)) {
      return { valid: false, message: "Phone number must be between 5 and 15 digits" };
    }
    return { valid: true, message: "" };
  }

  const length = phoneNumber.length;
  if (length < lengths.min || length > lengths.max) {
    if (lengths.min === lengths.max) {
      return { valid: false, message: `Phone number must be exactly ${lengths.min} digits for this country` };
    } else {
      return { valid: false, message: `Phone number must be between ${lengths.min} and ${lengths.max} digits for this country` };
    }
  }

  return { valid: true, message: "" };
};

/**
 * Get phone length constraints for a specific country
 * Useful for input validation UI (maxLength, minLength)
 */
export const getPhoneLengthForCountry = (countryCode: number): { min: number; max: number } => {
  return COUNTRY_PHONE_LENGTHS[countryCode] || { min: 5, max: 15 };
};

/**
 * Get all supported phone codes with their validation rules
 * Useful for debugging and documentation
 */
export const getAllPhoneValidationRules = (): { [key: number]: { min: number; max: number; description?: string } } => {
  const result: { [key: number]: { min: number; max: number; description?: string } } = {};

  Object.entries(COUNTRY_PHONE_PATTERNS).forEach(([code, rules]) => {
    result[parseInt(code, 10)] = rules;
  });

  // Add countries from countries-list that aren't in COUNTRY_PHONE_PATTERNS
  Object.entries(countries).forEach(([_, data]) => {
    const phones = Array.isArray(data.phone) ? data.phone : [data.phone];
    phones.forEach((phone) => {
      const phoneCode = typeof phone === "string" ? parseInt(phone, 10) : phone;
      if (!result[phoneCode]) {
        result[phoneCode] = { min: 8, max: 15 };
      }
    });
  });

  return result;
};
